

























































import { Component, Vue } from 'vue-property-decorator';
import { STORY_TEMPLATES } from '@/helpers/templateHelper';
import { prepareVariablesForSingleEntityQuery, redirectToSingleEntityRoute } from '@/helpers/graphqlHelper';
import { formatDate } from '@/helpers/dateTimeHelper';
import { contentTypeParam, getContentTypeFromEntityType } from '@/helpers/ga/params/contentTypeParam';
import { sendContentItemDetailsViewed, sendGaEvent } from '@/helpers/ga/gaSend';
import CommentAddedGaEvent from '@/helpers/ga/events/CommentAddedGaEvent';

import storyQuery from '@/graphql/story/Story.single.query.gql';
import storyCommentsQuery from '@/graphql/story/StoryComments.query.gql';
import addToFavoritesMutation from '@/graphql/me/favorite/AddToUserFavorites.mutation.gql';
import deleteFromUserFavoritesMutation from '@/graphql/me/favorite/DeleteFromUserFavorites.mutation.gql';
import addCommentMutation from '@/graphql/comment/AddComment.mutation.gql';

import AddToFavoriteAction from '@/components/partials/actions/AddToFavoriteAction.vue';
import AddToItineraryAction from '@/components/partials/actions/AddToItineraryAction.vue';
import MsgEntityNotFound from '@/components/MsgEntityNotFound.vue';
import AppContentBlockHero from '@/components/content-blocks/AppContentBlockHero.vue';
import AppPageSection from '@/components/AppPageSection.vue';
import AppContainer from '@/components/AppContainer.vue';
import AppSeparatorLine from '@/components/AppSeparatorLine.vue';
import AppReviewableSection from '@/components/AppReviewableSection.vue';
import miniCultivistsPageFilterQuery from '@/graphql/miniCultivist/MiniCultivistPageFilter.query.gql';

@Component({
  components: {
    AppReviewableSection,
    AppSeparatorLine,
    AppContainer,
    AppPageSection,
    AppContentBlockHero,
    MsgEntityNotFound,
    AddToItineraryAction,
    AddToFavoriteAction,
    StoryDefaultTemplate: () => import('@/components/partials/StoryDefaultTemplate.vue'),
    StoryCampaignGridTemplate: () => import('@/components/partials/StoryCampaignGridTemplate.vue'),
    StoryCampaignGroupsTemplate: () => import('@/components/partials/StoryCampaignGroupsTemplate.vue'),
    AppContentBlock: () => import('@/components/AppContentBlock.vue'),
    AppRelatedSection: () => import('@/components/partials/AppRelatedSection.vue'),
  },
  metaInfo() {
    return {
      title: this.story?.title || 'Story',
    };
  },
  beforeRouteUpdate(to, from, next) {
    this.story = null;
    this.storyExists = false;
    this.isFavoriteState = false;
    this.entityDataFetching = true;
    next();
  },
  apollo: {
    story: {
      query: storyQuery,
      variables() {
        return this.queryVariables;
      },
      result() {
        if (this.queryVariables.onlyId && this.story?.id) {
          this.$router.replace(
            redirectToSingleEntityRoute(this.$route.name, this.story.id, this.queryVariables.slug, {
              query: this.$route.query,
            })
          );
          return;
        }

        this.entityDataFetching = false;

        if (this.story?.id) {
          this.storyExists = true;
          this.isFavoriteState = this.story.is_favorite_by_current_user;
          this.comments = this.story.comments;
          sendContentItemDetailsViewed(contentTypeParam.story, this.story);
        }
      },
    },
  },
})
export default class StorySinglePage extends Vue {
  availableTemplates: any = STORY_TEMPLATES;
  entityDataFetching = true;
  isFavoriteState = false;
  story: any = null;
  comments: any[] = [];
  storyExists = false;

  get heroBlock() {
    const uid = `app-story-hero-${this.story.id}`;
    const storyMedia = this.story.show_media_cover ? this.story.media?.[0] : null;
    const media = storyMedia
      ? {
          type: 'image',
          images: [
            {
              id: `${uid}-image-${storyMedia.id}`,
              media: storyMedia,
            },
          ],
        }
      : null;

    return {
      uid,
      data: {
        heading: {
          value: this.story.title,
          tagLine: this.publishedAt,
        },
        media,
      },
    };
  }

  get queryVariables() {
    return prepareVariablesForSingleEntityQuery(this.$route.params);
  }

  get isCampaignGridItemsTemplate() {
    return this.story?.template === this.availableTemplates.campaignGridItems;
  }

  get isCampaignGroupsTemplate() {
    return this.story?.template === this.availableTemplates.campaignGroups;
  }

  get hasContentBlocks() {
    return this.story.contentBlocks.length > 0;
  }

  get media(): string {
    return this.story.show_media_cover ? this.story.media?.[0]?.url : '';
  }

  get publishedAt() {
    return formatDate(this.story.startPublishingAt);
  }

  handleFavoriteAction() {
    if (!this.isFavoriteState) {
      this.$apollo
        .mutate({
          mutation: addToFavoritesMutation,
          variables: {
            entities: [{ id: this.story.id, type: this.story.__typename }],
          },
        })
        .then((result) => {
          this.isFavoriteState = result.data.addToUserFavorites;
        });
      return;
    }

    this.$apollo
      .mutate({
        mutation: deleteFromUserFavoritesMutation,
        variables: {
          entities: [{ id: this.story.id, type: this.story.__typename }],
        },
      })
      .then((result) => {
        this.isFavoriteState = !result.data.deleteFromUserFavorites;
      });
  }

  storeReviewableItem(text: string) {
    this.$apollo
      .mutate({
        mutation: addCommentMutation,
        variables: {
          text,
          entityType: this.story.__typename,
          entityId: this.story.id,
        },
      })
      .then(() => {
        this.fetchComments();

        const gaEvent = new CommentAddedGaEvent()
          .addContentType(getContentTypeFromEntityType(this.story.__typename))
          .addContentId(this.story.id)
          .addContentName(this.story.title);
        sendGaEvent(gaEvent);
      });
  }

  fetchComments() {
    this.$apollo
      .query({
        query: storyCommentsQuery,
        fetchPolicy: 'no-cache',
        variables: {
          id: this.story.id,
        },
      })
      .then(({ data }) => {
        this.comments = data.storyComments.comments;
      });
  }
}
